/**
 * What the TikTok Events API needs from the browser to match a server event
 * to the ad click (server/utils/tiktokEvents.ts): the pixel's `_ttp` cookie
 * and the `ttclid` TikTok adds to ad links. Both only with cookie consent;
 * without it nothing is kept and the server sends nothing.
 */

const CLICK_ID_KEY = 'imr_ttclid'
/** TikTok's longest click attribution window. */
const CLICK_ID_DAYS = 28

export interface TiktokCheckoutContext {
  ttp?: string
  ttclid?: string
}

/** Stores the ttclid of an ad click, once the visitor has given consent. */
export function rememberTiktokClickId(ttclid: string): void {
  try {
    localStorage.setItem(CLICK_ID_KEY, JSON.stringify({ id: ttclid, at: Date.now() }))
  } catch {
    // Private mode: the server event still matches on _ttp and the e-mail.
  }
}

export function forgetTiktokClickId(): void {
  try {
    localStorage.removeItem(CLICK_ID_KEY)
  } catch {
    // Nothing stored.
  }
}

function storedClickId(): string | undefined {
  try {
    const stored = JSON.parse(localStorage.getItem(CLICK_ID_KEY) || 'null') as { id?: string, at?: number } | null
    if (!stored?.id || !stored.at || Date.now() - stored.at > CLICK_ID_DAYS * 86400_000) return undefined
    return stored.id
  } catch {
    return undefined
  }
}

/** Sent with the checkout; undefined (no consent) tells the server to send nothing. */
export function tiktokCheckoutContext(): TiktokCheckoutContext | undefined {
  if (!import.meta.client || !hasCookieConsent()) return undefined
  const ttp = document.cookie.match(/(?:^|;\s*)_ttp=([^;]+)/)?.[1]
  return {
    ...(ttp && { ttp: decodeURIComponent(ttp) }),
    ...(storedClickId() && { ttclid: storedClickId() }),
  }
}
